import { useEffect, useState } from 'react';
import { NodeSupplierContract } from '@prisma/client';
import { SelectProps, Spin } from 'antd';
import { debounce } from 'lodash';

import { useFindNodeSupplierContracts } from '../data';

type UseContractPickerOptions = {
  labelInValue?: boolean;
  mode?: SelectProps['mode'];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  value?: any;
};

export const useContractPicker = (config?: UseContractPickerOptions) => {
  const [search, setSearch] = useState(
    config?.value && !Array.isArray(config?.value) && config?.labelInValue !== false
      ? config?.value?.label?.split('|')?.[0] ?? ''
      : '',
  );

  useEffect(() => {
    if ((config?.mode === 'multiple') !== Array.isArray(config?.value))
      console.error('ContractPicker: Data type of value does not match the selection mode.');
  }, [config?.mode, config?.value]);

  const { data, isFetching, isPending } = useFindNodeSupplierContracts<
    Pick<NodeSupplierContract, 'id' | 'contractName' | 'contractNumber'>
  >({
    where: {
      OR: [
        { contractName: { contains: search.trim(), mode: 'insensitive' } },
        { contractNumber: { contains: search.trim() } },
      ],
    },
    take: 50,
    skip: 0,
    orderBy: { contractName: 'asc' },
  });

  const options = data?.data.map((x) => ({
    label: `${x.contractName} | ${x.contractNumber}`,
    value: x.contractNumber,
  }));

  const getSelectProps = (
    props?: Omit<
      SelectProps,
      'labelInValue' | 'loading' | 'mode' | 'notFoundContent' | 'options' | 'showSearch' | 'onSearch'
    >,
  ) => ({
    style: { minWidth: 600 },
    allowClear: true,
    filterOption: false,
    labelInValue: config?.labelInValue ?? true,
    loading: isFetching || isPending,
    mode: config?.mode,
    notFoundContent: isFetching || isPending ? <Spin size="small" /> : null,
    options,
    showSearch: true,
    onSearch: debounce(setSearch, 300),
    ...props,
  });

  return { getSelectProps };
};
